import { Box } from "@outschool/backpack";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { I18nLocale, useLocale, useTranslation } from "@outschool/localization";
import React from "react";

import { SetFiltersType } from "../../lib/useSearchFilters";
import { SquareButton } from "./SearchToggleButtons";

enum PresetSearchFilters {
  CLASSES_UNDER_15 = "classes-under-15",
  BEFORE_SCHOOL = "before-school",
  AFTER_SCHOOL = "after-school",
  WEEKEND = "weekend",
  BOOK_CLUB = "book-club",
  ONE_ON_ONE = "one-on-one",
  ESL = "esl",
  HARRY_POTTER = "harry-potter",
  WRITING = "writing",
  CHESS = "chess",
  READING_COMPREHENSION = "reading-comprehension",
  ENGLISH_DEBATE = "english-debate",
  SCIENCE_AND_NATURE = "science-and-nature",
  STARTING_SOON = "starting-soon",
  POKEMON = "pokemon",
  PRINCESS = "princess",
  MINECRAFT = "minecraft",
}

const presetFiltersByLocale: Record<I18nLocale, PresetSearchFilters[] | null> =
  {
    [I18nLocale.Ko]: [
      PresetSearchFilters.CLASSES_UNDER_15,
      PresetSearchFilters.BEFORE_SCHOOL,
      PresetSearchFilters.AFTER_SCHOOL,
      PresetSearchFilters.WEEKEND,
      PresetSearchFilters.BOOK_CLUB,
      PresetSearchFilters.ONE_ON_ONE,
      PresetSearchFilters.ESL,
      PresetSearchFilters.HARRY_POTTER,
      PresetSearchFilters.WRITING,
      PresetSearchFilters.CHESS,
    ],
    [I18nLocale.Ja]: [
      PresetSearchFilters.CLASSES_UNDER_15,
      PresetSearchFilters.BEFORE_SCHOOL,
      PresetSearchFilters.AFTER_SCHOOL,
      PresetSearchFilters.WEEKEND,
      PresetSearchFilters.BOOK_CLUB,
      PresetSearchFilters.ONE_ON_ONE,
      PresetSearchFilters.ESL,
      PresetSearchFilters.POKEMON,
      PresetSearchFilters.PRINCESS,
      PresetSearchFilters.MINECRAFT,
    ],
    [I18nLocale.ZhTw]: [
      PresetSearchFilters.CLASSES_UNDER_15,
      PresetSearchFilters.BEFORE_SCHOOL,
      PresetSearchFilters.AFTER_SCHOOL,
      PresetSearchFilters.WEEKEND,
      PresetSearchFilters.WRITING,
      PresetSearchFilters.READING_COMPREHENSION,
      PresetSearchFilters.ENGLISH_DEBATE,
      PresetSearchFilters.SCIENCE_AND_NATURE,
      PresetSearchFilters.STARTING_SOON,
    ],
    [I18nLocale.En]: null,
    [I18nLocale.Es]: null,
  };

export default function SearchToggleButtons({
  setSearchFilters,
  clearDropdowns,
}: {
  setSearchFilters: SetFiltersType;
  clearDropdowns: () => void;
}) {
  const { t } = useTranslation(
    "client\\components\\search\\EastAsiaSearchToggleButtons",
  );

  const handleFilterChange = (presetFilter: PresetSearchFilters) => {
    clearDropdowns();
    const filtersToSet: SearchFiltersType = {
      format: "Live online",
      q: null,
      endByTime: null,
      startAfterTime: null,
      dow: null,
      capacityMax: null,
      pricePerMeetingMax: null,
      order: null,
    };
    switch (presetFilter) {
      case PresetSearchFilters.CLASSES_UNDER_15:
        filtersToSet.pricePerMeetingMax = 15;
        break;
      case PresetSearchFilters.BEFORE_SCHOOL:
        filtersToSet.endByTime = 8.5;
        filtersToSet.dow = "Mon,Tue,Wed,Thu,Fri";
        break;
      case PresetSearchFilters.AFTER_SCHOOL:
        filtersToSet.startAfterTime = 13;
        filtersToSet.dow = "Mon,Tue,Wed,Thu,Fri";
        break;
      case PresetSearchFilters.WEEKEND:
        filtersToSet.dow = "Sat,Sun";
        break;
      case PresetSearchFilters.BOOK_CLUB:
        filtersToSet.q = "book club";
        break;
      case PresetSearchFilters.ONE_ON_ONE:
        filtersToSet.capacityMax = 1;
        break;
      case PresetSearchFilters.ESL:
        filtersToSet.q = "ESL";
        break;
      case PresetSearchFilters.HARRY_POTTER:
        filtersToSet.q = "Harry Potter";
        break;
      case PresetSearchFilters.WRITING:
        filtersToSet.q = "writing";
        break;
      case PresetSearchFilters.CHESS:
        filtersToSet.q = "chess";
        break;
      case PresetSearchFilters.READING_COMPREHENSION:
        filtersToSet.q = "reading comprehension";
        break;
      case PresetSearchFilters.ENGLISH_DEBATE:
        filtersToSet.q = "English debate";
        break;
      case PresetSearchFilters.SCIENCE_AND_NATURE:
        filtersToSet.q = "science and nature";
        break;
      case PresetSearchFilters.STARTING_SOON:
        filtersToSet.order = "upcoming";
        break;
      case PresetSearchFilters.POKEMON:
        filtersToSet.q = "pokemon";
        break;
      case PresetSearchFilters.PRINCESS:
        filtersToSet.q = "princess";
        break;
      case PresetSearchFilters.MINECRAFT:
        filtersToSet.q = "minecraft";
        break;
    }
    setSelectedPresetFilter(presetFilter);
    setSearchFilters("preset", filtersToSet);
  };
  const [selectedPresetFilter, setSelectedPresetFilter] =
    React.useState<PresetSearchFilters | null>(null);

  const locale = useLocale();
  const filtersForLocale = presetFiltersByLocale[locale];
  if (!filtersForLocale) {
    return null;
  }
  const labels: Record<PresetSearchFilters, string> = {
    [PresetSearchFilters.CLASSES_UNDER_15]: t("Classes under $15"),
    [PresetSearchFilters.BEFORE_SCHOOL]: t("Before school"),
    [PresetSearchFilters.AFTER_SCHOOL]: t("After school"),
    [PresetSearchFilters.WEEKEND]: t("Weekend"),
    [PresetSearchFilters.BOOK_CLUB]: t("Book club"),
    [PresetSearchFilters.ONE_ON_ONE]: t("One-on-one"),
    [PresetSearchFilters.ESL]: t("ESL"),
    [PresetSearchFilters.HARRY_POTTER]: t("Harry Potter"),
    [PresetSearchFilters.WRITING]: t("Writing"),
    [PresetSearchFilters.CHESS]: t("Chess"),
    [PresetSearchFilters.READING_COMPREHENSION]: t("Reading comprehension"),
    [PresetSearchFilters.ENGLISH_DEBATE]: t("English debate"),
    [PresetSearchFilters.SCIENCE_AND_NATURE]: t("Science and nature"),
    [PresetSearchFilters.STARTING_SOON]: t("Starting soon"),
    [PresetSearchFilters.POKEMON]: t("Pokemon"),
    [PresetSearchFilters.PRINCESS]: t("Princess"),
    [PresetSearchFilters.MINECRAFT]: t("Minecraft"),
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5em",
      }}
    >
      {filtersForLocale.map(presetFilter => (
        <SquareButton
          isSelected={selectedPresetFilter === presetFilter}
          onClick={() => handleFilterChange(presetFilter)}
          toggleName={presetFilter}
        >
          {labels[presetFilter]}
        </SquareButton>
      ))}
    </Box>
  );
}
